import { ColumnDef } from '@tanstack/react-table';
import * as XLSX from 'xlsx';

export function getCellValue<T>(row: T, column: ColumnDef<T>): string | number | Date | null | unknown {
  try {
    let value;
    if ('accessorFn' in column && typeof column.accessorFn === 'function') {
      value = column.accessorFn(row, 0);
    } else if ('accessorKey' in column) {
      const key = column.accessorKey as string | keyof T;
      value = row[key as keyof T];
    } else if (column.id) {
      value = row[column.id as keyof T];
    }

    if (value === null || value === undefined) {
      return '';
    }

    return value;
  } catch (error) {
    console.error('Error getting cell value:', error);
    return '';
  }
}

export function getHeaderValue<T>(column: ColumnDef<T>, t: (key: string) => string): string {
  if (typeof column.header === 'string') {
    return t(column.header);
  }

  return t((column.id || ('accessorKey' in column ? column.accessorKey : '')) as string);
}

function isValidDate(value: unknown): boolean {
  if (value instanceof Date) return !isNaN(value.getTime());
  
  if (typeof value === 'string') {
    const datePattern = /^\d{1,2}[/\-.]\d{1,2}[/\-.]\d{2,4}$|^\d{4}[/\-.]\d{1,2}[/\-.]\d{1,2}$|^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
    
    if (!datePattern.test(value.trim())) {
      return false;
    }
    
    const date = new Date(value);
    return !isNaN(date.getTime());
  }
  
  return false;
}

const CURRENCY_FORMATS = {
  '₪': '[$₪-40D] #,##0.00',
  '$': '[$$-409] #,##0.00'
};

function processCurrencyValue(value: string): { isValid: boolean; numericValue?: number; format?: string } {
  if (typeof value !== 'string' || !value.match(/[$₪]/)) {
    return { isValid: false };
  }
  
  const isNegative = value.includes('-');
  
  const cleanValue = value.replace(/[^0-9.]/g, '').trim();
  
  let numericValue = parseFloat(cleanValue);
  if (isNegative) {
    numericValue = -numericValue;
  }
  
  if (!cleanValue.length || isNaN(numericValue) || !isFinite(numericValue)) {
    return { isValid: false };
  }
  
  const isShekels = value.includes('₪');
  const currencySymbol = isShekels ? '₪' : '$';
  
  return {
    isValid: true,
    numericValue,
    format: CURRENCY_FORMATS[currencySymbol]
  };
}

export function getExportData<T>(data: T[], columns: ColumnDef<T>[], t: (key: string) => string) {
  const headers = columns.map((column) => getHeaderValue(column, t));
  const rows = data.map((row) => columns.map((column) => getCellValue(row, column)));

  return {
    headers,
    rows,
  };
}

export function exportToExcel<T>(data: T[], columns: ColumnDef<T>[], filename: string, t: (key: string) => string) {
  try {
    const { headers, rows } = getExportData(data, columns, t);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    
    wb.Workbook = wb.Workbook || { Sheets: [], Views: [{ RTL: true }] };
    wb.Workbook.Views = wb.Workbook.Views || [{ RTL: true }];
    
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    for (let R = 1; R <= range.e.r; ++R) { 
      for (let C = 0; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ c: C, r: R });
        const cell = ws[cellAddress];
        
        if (!cell || cell.v === null || cell.v === undefined) continue;
        
        const value = cell.v;
        
        if (isValidDate(value)) {
          cell.v = value instanceof Date ? value : new Date(value);
          cell.t = 'd';
          cell.z = 'dd/mm/yyyy';
        } 
        else if (typeof value === 'string') {
          const currencyData = processCurrencyValue(value);
          
          if (currencyData.isValid) {
            cell.v = currencyData.numericValue;
            cell.t = 'n';
            cell.z = currencyData.format;
          } else {
            cell.t = 's';
          }
        }
        else if (typeof value === 'number') {
          cell.t = 'n';
        }
        else {
          cell.t = 's';
        }
      }
    }
    ws['!cols'] = headers.map((header, colIndex) => {
      let maxWidth = String(header).length;
      for (const row of rows) {
        const content = row[colIndex];
        if (typeof content === 'number') {
          const contentLength = String(Math.abs(content)).length + (content < 0 ? 1 : 0);
          maxWidth = Math.max(maxWidth, contentLength + 4);
          continue;
        } 
        else if (typeof content === 'string') {
          const currencyData = processCurrencyValue(content);
          if (currencyData.isValid) {
            const numLength = String(Math.abs(currencyData.numericValue || 0)).length;
            const isNegative = (currencyData.numericValue || 0) < 0;
            const estimatedWidth = numLength + 5 + (isNegative ? 1 : 0);
            maxWidth = Math.max(maxWidth, estimatedWidth);
            continue;
          }
        }
        
        const contentLength = String(content || '').length;
        maxWidth = Math.max(maxWidth, contentLength);
      }
      
      return { wch: Math.min(Math.max(maxWidth + 2, 10), 40) };
    });
    
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, `${filename}.xlsx`);
  } catch (error) {
    console.error('Error generating Excel:', error);
  }
}
